import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SearchActions } from '../sagas/actions';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search, X } from 'lucide-react';

interface SearchInputBarProps {
    searchInputRef: any;
    /** Rendered on the home hero: taller field on a light surface. */
    hero?: boolean;
    /** Rendered inside a narrow rail: field only, no submit button. */
    compact?: boolean;
    minimized?: boolean;
}

const SearchInputBar = ({ searchInputRef, minimized, compact, hero }: SearchInputBarProps) => {
    const dispatch = useDispatch();

    const related = useSelector((state: any) => state.search.searchingRelated) ?? undefined;
    const searchTerm = useSelector((state: any) => state.search.query.query.replaceAll('*', '')) ?? '';
    const searching = useSelector((state: any) => state.search.searching) ?? false;
    const page = useSelector((state: any) => state.search.query.page) ?? 0;
    const selectedItem = useSelector((state: any) => state.search.selectedItem);
    const [inputValue, setInputValue] = useState(searchTerm);
    const navURL = useSelector((state: any) => state.search.navURL);
    const navigate = useNavigate();

    useEffect(() => {
        navigate(navURL);
    }, [navigate, minimized, navURL]);

    useEffect(() => {
        setInputValue(searchTerm);
    }, [searchTerm]);

    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setInputValue(value);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            dispatch({ type: SearchActions.SET_SEARCH_TERM, payload: value });
        }, 300);
    }, [dispatch]);

    const _search = (append?: boolean) => {
        if (!append) {
            if (!selectedItem) {
                dispatch({ type: SearchActions.SET_SEARCH_RELATED, payload: false });
                dispatch({ type: SearchActions.SET_SELECTED, payload: null });
                dispatch({ type: SearchActions.INITIAL_SEARCH });
                dispatch({ type: SearchActions.SET_SCROLLED_DOWN, payload: false });
            } else {
                dispatch({ type: SearchActions.RELATED_SEARCH_FILTERED });
                dispatch({ type: SearchActions.SET_SCROLLED_DOWN, payload: false });
            }
        } else {
            dispatch({ type: SearchActions.APPENDING_SEARCH });
            dispatch({ type: SearchActions.SET_SCROLLED_DOWN, payload: false });
        }
    }

    const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            _search();
        }
        if (event.key === 'ArrowDown') {
            dispatch({ type: SearchActions.SET_PAGE, payload: page + 1 });
        }
    };

    const handleSearchClick = () => _search();

    const handleClickSearchAll = () => {
        dispatch({ type: SearchActions.SET_SEARCH_RELATED, payload: false });
        dispatch({ type: SearchActions.CLEAR_SELECTED });
        searchInputRef.current?.focus();
    };

    const handleClearState = () => {
        dispatch({ type: SearchActions.CLEAR_STATE });
        searchInputRef.current?.focus();
    }

    const placeholder = related
        ? 'Search within results…'
        : 'Search datasets, cruises, DOIs, authors, areas…';

    const height = hero ? 'h-[52px]' : compact ? 'h-10' : 'h-11';
    const fieldSurface = hero
        ? 'bg-surface border-transparent shadow-float text-base'
        : 'bg-surface-2 border-line text-[15px]';

    return (
        <div className={`flex w-full gap-2.5 ${compact ? 'flex-col' : 'flex-row'}`}>
            <div className="relative min-w-0 flex-1">
                <label htmlFor="search" className="sr-only">Search</label>

                <span className="pointer-events-none absolute left-3.5 top-1/2 flex -translate-y-1/2 text-ink-3">
                    {searching
                        ? <Loader2 className={`${hero ? 'h-[18px] w-[18px]' : 'h-4 w-4'} animate-spin text-brand`} />
                        : <Search className={hero ? 'h-[18px] w-[18px]' : 'h-4 w-4'} />}
                </span>

                <input
                    ref={searchInputRef}
                    id="search"
                    type="text"
                    onChange={handleChange}
                    onKeyUp={handleKeyUp}
                    placeholder={placeholder}
                    className={`nz-input ${height} ${fieldSurface} rounded-lg pl-11 ${inputValue?.length > 0 ? 'pr-11' : 'pr-3'}`}
                    value={inputValue}
                    autoComplete="off"
                />

                {inputValue?.length > 0 && (
                    <button
                        type="button"
                        className="nz-icon-btn absolute right-2 top-1/2 h-7 w-7 -translate-y-1/2"
                        onClick={handleClearState}
                        title="Clear search"
                        aria-label="Clear search"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>

            {!compact && (
                <button
                    type="button"
                    onClick={handleSearchClick}
                    className={`nz-btn nz-btn-primary shrink-0 rounded-lg px-6 ${height} ${hero ? 'text-[15px] shadow-float' : ''}`}
                >
                    Search
                </button>
            )}

            {related && !searchTerm && (
                <button
                    type="button"
                    className="shrink-0 self-center text-[13px] font-medium text-brand hover:underline"
                    onClick={handleClickSearchAll}
                >
                    Search all data
                </button>
            )}
        </div>
    );
};

export default SearchInputBar;
